import { useMemo, useState } from "react";
import type { GeneratedDefinition } from "@keydials/api-client";
import { applyPreset, segmentNoun, type BuilderPresetId } from "@keydials/dial-workspace";
import { Dial } from "@keydials/ui";
import { coloursPreview, coloursView } from "../coloursView";
import { builderCopy as copy, fill, nounValues } from "../copy";
import type { DraftSession, DraftSnapshot } from "../draftSession";
import { StatusCustomiser } from "../StatusCustomiser";

/**
 * Colours (KEY-560): how people will mark progress. Three presets, previewed on the dial itself, and "Customise" for the states'
 * colours and names and which count as done. Every change is autosaved; a checklist dial's scoring scale follows these colours
 * without the author seeing it. A preset picked over states that match none of the three asks first, because those states would
 * be lost; moving between untouched presets loses nothing, so it doesn't.
 */
export function ColoursStage({ session, snapshot, onNext }: { session: DraftSession; snapshot: DraftSnapshot; onNext: () => void }) {
  const model = snapshot.model;
  const locked = snapshot.lockedLifecycle !== null;
  const view = coloursView(model);
  const [customising, setCustomising] = useState(view.customised);
  const [confirming, setConfirming] = useState<BuilderPresetId | null>(null);
  const words = nounValues(segmentNoun(snapshot.noun ? { segmentNounSingular: snapshot.noun.singular, segmentNounPlural: snapshot.noun.plural } : null));
  const preview = useMemo(() => coloursPreview(model), [model]);
  const statusLabels = Object.fromEntries(model.statusSet.statuses.map((s) => [s.key, s.name]));

  const edit = (change: (draft: GeneratedDefinition) => GeneratedDefinition) => {
    if (!locked) session.edit(change);
  };

  const choosePreset = (id: BuilderPresetId) => {
    if (view.customised) setConfirming(id);
    else edit((draft) => applyPreset(draft, id));
  };

  return (
    <section className="kd-colours" aria-labelledby="kd-builder-question">
      <h1 id="kd-builder-question" className="kd-builder-question">
        {copy.colours.question}
      </h1>
      <div className="kd-colours-grid">
        <div className="kd-colours-main">
          <div className="kd-preset-list">
            {view.cards.map((card) => (
              <button
                key={card.id}
                type="button"
                className="kd-choice kd-preset"
                aria-pressed={card.selected}
                disabled={locked}
                onClick={() => choosePreset(card.id)}
              >
                <span className="kd-choice-title">
                  {card.title}
                  {card.recommended && <span className="kd-preset-chip">{copy.start.recommended}</span>}
                </span>
                <span className="kd-preset-states">
                  {card.states.map((state) => (
                    <span key={state.key} className="kd-preset-state">
                      <span className="kd-preset-dot" style={{ background: state.colour }} aria-hidden="true" />
                      {state.name}
                    </span>
                  ))}
                </span>
              </button>
            ))}
          </div>

          {confirming && !locked && (
            <div className="kd-builder-confirm" role="alertdialog" aria-labelledby="kd-colours-confirm">
              <p id="kd-colours-confirm">{fill(copy.colours.presetConfirm, { name: copy.colours.presets[confirming] })}</p>
              <div className="kd-builder-actions">
                <button type="button" className="kd-btn" onClick={() => setConfirming(null)}>
                  {copy.colours.keepStates}
                </button>
                <button
                  type="button"
                  className="kd-btn-primary"
                  onClick={() => {
                    edit((draft) => applyPreset(draft, confirming));
                    setConfirming(null);
                  }}
                >
                  {copy.colours.presetSwitch}
                </button>
              </div>
            </div>
          )}

          {view.itemised && (
            <div className="kd-panel-help">
              <p>{fill(copy.colours.checklistRollup, words)}</p>
              <p>{copy.colours.reviewDates}</p>
            </div>
          )}

          <button type="button" className="kd-builder-link kd-colours-customise" aria-expanded={customising} onClick={() => setCustomising((open) => !open)}>
            {copy.colours.customise}
          </button>
          {customising && <StatusCustomiser statuses={model.statusSet.statuses} disabled={locked} edit={edit} />}
        </div>

        <div className="kd-colours-wheel">
          <Dial model={preview} fluid ariaLabel={model.name} statusLabels={statusLabels} />
        </div>
      </div>

      <div className="kd-builder-footer">
        <button type="button" className="kd-btn-primary" onClick={onNext}>
          {copy.frame.continue}
        </button>
      </div>
    </section>
  );
}
